import { useEffect, useId, useRef, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { CloseIcon } from '@/components/icons';
import { ActionIcon } from './ActionIcon';
import './Modal.css';

let openModalCount = 0;
let bodyOverflowBeforeModals = '';

/** Dialogue natif : focus contenu dans la modale et arrière-plan inerte. */
export const Modal = ({
  title,
  description,
  icon,
  children,
  onClose,
  closeDisabled = false,
  returnFocusRef,
  variant = 'center',
  footer,
  closing = false,
  onExitComplete,
  role = 'dialog',
  closeLabel = 'Fermer le formulaire',
}: {
  title: string;
  description?: string;
  icon?: ReactNode;
  children: ReactNode;
  onClose: () => void;
  closeDisabled?: boolean;
  returnFocusRef?: RefObject<HTMLElement | null>;
  variant?: 'center' | 'sheet' | 'confirmation' | 'compact' | 'avatar' | 'photo';
  footer?: ReactNode;
  closing?: boolean;
  onExitComplete?: () => void;
  role?: 'dialog' | 'alertdialog';
  closeLabel?: string;
}) => {
  const dialog = useRef<HTMLDialogElement>(null);
  const heading = useRef<HTMLHeadingElement>(null);
  const titleId = useId();
  const descriptionId = useId();
  useEffect(() => {
    const element = dialog.current;
    if (!element) return;
    const previousFocus =
      returnFocusRef?.current ??
      (document.activeElement instanceof HTMLElement ? document.activeElement : null);
    element.showModal();
    if (openModalCount === 0) bodyOverflowBeforeModals = document.body.style.overflow;
    openModalCount += 1;
    document.body.style.overflow = 'hidden';
    heading.current?.focus();
    return () => {
      element.close();
      openModalCount -= 1;
      if (openModalCount === 0) document.body.style.overflow = bodyOverflowBeforeModals;
      if (previousFocus?.isConnected) previousFocus.focus({ preventScroll: true });
    };
  }, [returnFocusRef]);
  useEffect(() => {
    if (!closing || !onExitComplete) return;
    const element = dialog.current;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      onExitComplete();
      return;
    }
    let completed = false;
    const finish = () => {
      if (completed) return;
      completed = true;
      onExitComplete();
    };
    const end = (event: AnimationEvent) => {
      if (event.target === element && event.animationName === 'hubeex-sheet-dismiss') finish();
    };
    element?.addEventListener('animationend', end);
    // Repli si l'animation est interrompue ou désactivée par le navigateur.
    const timer = window.setTimeout(finish, 360);
    return () => {
      window.clearTimeout(timer);
      element?.removeEventListener('animationend', end);
    };
  }, [closing, onExitComplete]);
  return createPortal(
    <dialog
      ref={dialog}
      className={`hubeex-modal${variant !== 'center' ? ` hubeex-modal--${variant}` : ''}`}
      role={role}
      data-closing={closing || undefined}
      inert={closing}
      aria-labelledby={titleId}
      aria-describedby={description ? descriptionId : undefined}
      onCancel={(event) => {
        event.preventDefault();
        if (!closeDisabled) onClose();
      }}
      onClick={(event) => {
        if (closeDisabled || event.target !== event.currentTarget) return;
        const rect = event.currentTarget.getBoundingClientRect();
        if (
          event.clientX < rect.left ||
          event.clientX > rect.right ||
          event.clientY < rect.top ||
          event.clientY > rect.bottom
        )
          onClose();
      }}
    >
      <div className="hubeex-modal__header">
        {icon ? (
          <span className="hubeex-modal__icon" aria-hidden>
            {icon}
          </span>
        ) : null}
        <div className="hubeex-modal__heading">
          <h2 id={titleId} ref={heading} tabIndex={-1}>
            {title}
          </h2>
          {description ? <p id={descriptionId}>{description}</p> : null}
        </div>
        <ActionIcon label={closeLabel} disabled={closeDisabled} onClick={onClose}>
          <CloseIcon />
        </ActionIcon>
      </div>
      <div className="hubeex-modal__body">{children}</div>
      {footer ? <div className="hubeex-modal__footer">{footer}</div> : null}
    </dialog>,
    document.body,
  );
};
